<script lang="ts">
  import JsonViewer from './JsonViewer.svelte';

  interface Props {
    data: any;
    maxDepth?: number;
    collapsed?: boolean;
    _depth?: number;
    _keyName?: string;
  }

  let { data, maxDepth = 3, collapsed = false, _depth = 0, _keyName = '' }: Props = $props();

  let manualToggle = $state<boolean | null>(null);
  let showCopy = $state(false);
  let copyFeedback = $state(false);

  const isCollapsed = $derived(manualToggle ?? (collapsed || _depth >= maxDepth));

  function toggle() {
    manualToggle = manualToggle === null ? !(collapsed || _depth >= maxDepth) : !manualToggle;
  }

  const dataType = $derived(
    data === null ? 'null'
    : Array.isArray(data) ? 'array'
    : typeof data
  );

  const isExpandable = $derived(dataType === 'object' || dataType === 'array');
  const entries = $derived(isExpandable && data ? Object.entries(data) : []);
  const previewText = $derived(
    dataType === 'array' ? `Array(${(data as any[]).length})`
    : dataType === 'object' ? `{${Object.keys(data).length} keys}`
    : ''
  );

  async function copyJson() {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    copyFeedback = true;
    setTimeout(() => copyFeedback = false, 1200);
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="font-mono text-xs leading-relaxed {_depth === 0 ? 'relative' : ''}"
  onmouseenter={() => { if (_depth === 0) showCopy = true; }}
  onmouseleave={() => { if (_depth === 0) showCopy = false; }}
>
  {#if _depth === 0 && showCopy}
    <button
      onclick={copyJson}
      class="absolute -top-1 right-0 px-2 py-0.5 rounded text-[10px] bg-bg-card border border-border text-text-secondary hover:text-accent transition-colors z-10"
      aria-label="Copy JSON"
    >
      {copyFeedback ? '✓ Copied' : 'Copy'}
    </button>
  {/if}

  {#if isExpandable}
    <span class="inline">
      {#if _keyName}
        <span class="text-accent">"{_keyName}"</span><span class="text-text-muted">: </span>
      {/if}
      <button
        onclick={toggle}
        class="text-text-muted hover:text-text-primary cursor-pointer select-none transition-colors"
        aria-label="{isCollapsed ? 'Expand' : 'Collapse'}"
        aria-expanded={!isCollapsed}
      >
        <span class="inline-block w-3 text-center transition-transform {isCollapsed ? '' : 'rotate-90'}">▶</span>
      </button>
      <span class="text-text-muted">{dataType === 'array' ? '[' : '{'}</span>
      {#if isCollapsed}
        <span class="text-text-muted italic ml-1">{previewText}</span>
        <span class="text-text-muted">{dataType === 'array' ? ']' : '}'}</span>
      {/if}
    </span>

    {#if !isCollapsed}
      <div class="ml-4 border-l border-border-light pl-2">
        {#each entries as [key, value], i (key)}
          {#if typeof value === 'object' && value !== null}
            <JsonViewer data={value} {maxDepth} {collapsed} _depth={_depth + 1} _keyName={dataType === 'array' ? '' : key} />
          {:else}
            <div class="py-px">
              {#if dataType !== 'array'}
                <span class="text-accent">"{key}"</span><span class="text-text-muted">: </span>
              {/if}
              {#if typeof value === 'string'}
                <span class="text-success">"{value}"</span>
              {:else if typeof value === 'number'}
                <span class="text-warning">{value}</span>
              {:else if typeof value === 'boolean'}
                <span class="text-[#A855F7]">{value}</span>
              {:else if value === null}
                <span class="text-error">null</span>
              {:else}
                <span class="text-text-secondary">{String(value)}</span>
              {/if}
              {#if i < entries.length - 1}<span class="text-text-muted">,</span>{/if}
            </div>
          {/if}
        {/each}
      </div>
      <span class="text-text-muted">{dataType === 'array' ? ']' : '}'}</span>
    {/if}

  {:else}
    <span class="inline">
      {#if _keyName}
        <span class="text-accent">"{_keyName}"</span><span class="text-text-muted">: </span>
      {/if}
      {#if typeof data === 'string'}
        <span class="text-success">"{data}"</span>
      {:else if typeof data === 'number'}
        <span class="text-warning">{data}</span>
      {:else if typeof data === 'boolean'}
        <span class="text-[#A855F7]">{data}</span>
      {:else if data === null || data === undefined}
        <span class="text-error">{String(data)}</span>
      {:else}
        <span class="text-text-secondary">{String(data)}</span>
      {/if}
    </span>
  {/if}
</div>
